import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, TextField, MenuItem, Box, Chip, IconButton, Alert, CircularProgress, Stack
} from '@mui/material';
import { Close, Download, Add, Delete } from '@mui/icons-material';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import api from '../utils/Api';
import TransactionPDF from './TransactionPDF';

const SchoolTransactionsModal = ({ open, onClose, school }) => {
    const [transactions, setTransactions] = useState([]);
    const [schoolInfoProfile, setSchoolInfoProfile] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [view, setView] = useState('list'); // 'list' | 'create'
    const [form, setForm] = useState({ 
        type: 'payment', 
        items: [{ description: '', amount: '' }], 
        status: 'paid', 
        dueDate: '' 
    });

    const fetchTransactions = useCallback(async () => {
        if (!school?._id) return;
        try {
            setLoading(true);
            const res = await api.get(`/super-admin/schools/${school._id}/transactions`);
            setTransactions(res.data.transactions || []);
            setSchoolInfoProfile(res.data.schoolInfo || {});
        } catch (err) {
            console.error(err);
            setError("Failed to fetch transactions");
        } finally {
            setLoading(false);
        }
    }, [school?._id]);

    useEffect(() => {
        if (open && school) {
            setView('list');
            fetchTransactions();
        }
    }, [open, school, fetchTransactions]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const totalAmount = form.items.reduce((sum, item) => sum + Number(item.amount || 0), 0);
            const payload = {
                ...form,
                amount: totalAmount,
                description: form.items.length > 1 ? 'Multiple Items' : form.items[0].description
            };
            await api.post(`/super-admin/schools/${school._id}/transactions`, payload);
            setForm({ type: 'payment', items: [{ description: '', amount: '' }], status: 'paid', dueDate: '' });
            setView('list');
            fetchTransactions();
        } catch (err) {
            console.error(err);
            setError("Failed to create transaction");
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await api.put(`/super-admin/transactions/${id}/status`, { status: newStatus });
            fetchTransactions();
        } catch (err) {
            console.error(err);
            alert("Failed to update status");
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...form.items];
        newItems[index][field] = value;
        setForm({ ...form, items: newItems });
    };

    const handleAddItem = () => {
        setForm({ ...form, items: [...form.items, { description: '', amount: '' }] });
    };

    const handleRemoveItem = (index) => {
        const newItems = form.items.filter((_, i) => i !== index);
        setForm({ ...form, items: newItems });
    };

    if (!school) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Transactions: {school.name}</Typography>
                    <IconButton onClick={onClose} size="small"><Close /></IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {view === 'list' ? (
                    <>
                        <Box display="flex" justifyContent="flex-end" mb={2}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setView('create')}
                            >
                                New Transaction
                            </Button>
                        </Box>
                        {loading ? <CircularProgress /> : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transactions.map(t => (
                                            <TableRow key={t._id}>
                                                <TableCell>{new Date(t.createdAt).toLocaleDateString()}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={t.type.toUpperCase()} color={t.type === 'payment' ? 'success' : 'primary'} variant="outlined" />
                                                </TableCell>
                                                <TableCell>{t.description}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>₵ {t.amount}</TableCell>
                                                <TableCell>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={t.status}
                                                        onChange={(e) => handleStatusChange(t._id, e.target.value)}
                                                        variant="standard"
                                                        InputProps={{ disableUnderline: true }}
                                                    >
                                                        <MenuItem value="pending">Pending</MenuItem>
                                                        <MenuItem value="paid">Paid</MenuItem>
                                                        <MenuItem value="cancelled">Cancelled</MenuItem>
                                                    </TextField>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <PDFDownloadLink
                                                        document={<TransactionPDF transaction={t} school={school} schoolInfoProfile={schoolInfoProfile} />}
                                                        fileName={`${t.type}_${school.name.replace(/\s+/g, '')}_${t._id.slice(-4)}.pdf`}
                                                    >
                                                        {({ loading }) => (
                                                            <IconButton size="small" color="primary" disabled={loading} title="Download PDF">
                                                                <Download fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </PDFDownloadLink>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {transactions.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">No transactions found.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3} mt={1}>
                            <TextField
                                select
                                label="Transaction Type"
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value, status: e.target.value === 'payment' ? 'paid' : 'pending' })}
                                fullWidth
                                required
                            >
                                <MenuItem value="invoice">Invoice</MenuItem>
                                <MenuItem value="payment">Payment Receipt</MenuItem>
                            </TextField>

                            <Typography variant="subtitle2" fontWeight="bold">Transaction Items</Typography>
                            {form.items.map((item, index) => (
                                <Stack direction="row" spacing={2} key={index} alignItems="flex-start">
                                    <TextField
                                        label="Description"
                                        value={item.description}
                                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                        fullWidth
                                        required
                                        size="small"
                                        placeholder="e.g. Term 1 Subscription"
                                    />
                                    <TextField
                                        type="number"
                                        label="Amount"
                                        value={item.amount}
                                        onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                                        sx={{ width: 150 }}
                                        required
                                        size="small"
                                        inputProps={{ min: 0, step: "0.01" }}
                                    />
                                    <IconButton 
                                        color="error" 
                                        onClick={() => handleRemoveItem(index)}
                                        disabled={form.items.length === 1}
                                        size="small"
                                        sx={{ mt: 0.5 }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Stack>
                            ))}

                            <Button 
                                startIcon={<Add />} 
                                onClick={handleAddItem} 
                                size="small" 
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                Add Item
                            </Button>

                            <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1, color: 'white' }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Total Amount: ₵ {form.items.reduce((sum, item) => sum + Number(item.amount || 0), 0).toFixed(2)}
                                </Typography>
                            </Box>

                            {form.type === 'invoice' && (
                                <TextField
                                    type="date"
                                    label="Due Date"
                                    value={form.dueDate}
                                    onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}

                            <TextField
                                select
                                label="Status"
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                                fullWidth
                                required
                            >
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="paid">Paid</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </TextField>
                        </Stack>
                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                            <Button onClick={() => setView('list')}>Cancel</Button>
                            <Button type="submit" variant="contained">Save {form.type === 'invoice' ? 'Invoice' : 'Receipt'}</Button>
                        </Box>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SchoolTransactionsModal;
